import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Box,
  Typography,
  Divider,
  TextField,
  Paper,
  Snackbar,
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import { makeStyles } from '@material-ui/core/styles';
import CardGiftcardIcon from '@material-ui/icons/CardGiftcard';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import moment from 'moment';
import colors from '../../../../theme/colors';
import API from '../../../../network/dashboardAPI';

const useStyles = makeStyles((theme) => ({
  summaryTable: {
    '& td': { padding: '6px 12px', borderBottom: '1px solid #e0e0e0', borderTop: 'none', borderLeft: 'none', borderRight: 'none' },
  },
  summaryLabel: {
    color: '#555',
    width: 220,
  },
  summaryValue: {
    fontWeight: 600,
    color: '#222',
  },
  redeem: {
    color: '#e53935',
  },
  totalRow: {
    borderTop: '2px solid #ddd',
    '& td': { fontWeight: 700 },
  },
  txTable: {
    '& th': { fontWeight: 700, backgroundColor: '#f5f5f5', padding: '8px 12px', borderBottom: '1px solid #e0e0e0', borderTop: 'none', borderLeft: 'none', borderRight: 'none' },
    '& td': { padding: '6px 12px', borderBottom: '1px solid #e0e0e0', borderTop: 'none', borderLeft: 'none', borderRight: 'none' },
  },
  tierBox: {
    backgroundColor: '#f9f9f9',
    border: '1px solid #e0e0e0',
    borderRadius: 6,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  itemTable: {
    '& th': { fontWeight: 700, backgroundColor: '#f5f5f5', padding: '6px 10px', whiteSpace: 'nowrap', borderBottom: '1px solid #e0e0e0', borderTop: 'none', borderLeft: 'none', borderRight: 'none' },
    '& td': { padding: '5px 10px', borderBottom: '1px solid #e0e0e0', borderTop: 'none', borderLeft: 'none', borderRight: 'none' },
  },
  promoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
}));

const DeliPointsDialog = ({ open, onClose, details, orderId }) => {
  const classes = useStyles();
  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(false);
  const [customerDp, setCustomerDp] = useState(null);
  const [promoPoints, setPromoPoints] = useState('');
  const [savingPromo, setSavingPromo] = useState(false);
  const [itemEdits, setItemEdits] = useState({});
  const [saveAlert, setSaveAlert] = useState({ open: false, success: true, message: '' });
  const [pendingTx, setPendingTx] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, tx: null, status: null });
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    if (!open) return;
    console.log('DeliPoints details:', details);
    setPromoPoints(details?.dp_promo_points_earned ?? '');
    const initEdits = {};
    (details?.order_item_details ?? []).forEach((item, i) => {
      const key = item.order_item_id ?? item.id ?? i;
      initEdits[key] = {
        dp_product_multiplier: item.dp_product_multiplier ?? '',
        dp_fix_points_promo_item: item.dp_fix_points_promo_item ?? '',
        dp_points_redeemed: item.dp_points_redeemed ?? '',
        dp_discount_payment: item.dp_discount_payment ?? '',
      };
    });
    setItemEdits(initEdits);
    fetchTransactions();
    fetchCustomerDp();
    fetchPendingTransactions();
  }, [open]);

  const fetchPendingTransactions = async () => {
    if (!orderId) return;
    try {
      const response = await API.getOrderPendingDelipointsTransactions({ order_id: orderId });
      const resp = await response.json();
      if (resp.success) {
        setPendingTx(resp.data?.transactions ?? []);
      }
    } catch (e) {
      console.error('Failed to fetch pending delipoint transactions', e);
    }
  };

  const handleConfirmPending = async () => {
    const { tx, status } = confirmDialog;
    if (!tx || !status) return;
    setApproving(true);
    try {
      const response = await API.approvePendingDelipointsTransaction({ transaction_id: tx.id, status });
      const resp = await response.json();
      if (resp.success) {
        setConfirmDialog({ open: false, tx: null, status: null });
        setSaveAlert({
          open: true,
          success: true,
          message: status === 'approved' ? 'Refund approved.' : 'Refund rejected.',
        });
        setTimeout(() => window.location.reload(), 1000);
      } else {
        setSaveAlert({ open: true, success: false, message: resp.message || 'Failed to update pending transaction.' });
      }
    } catch (e) {
      console.error('Failed to update pending delipoint transaction', e);
      setSaveAlert({ open: true, success: false, message: 'An error occurred while updating.' });
    } finally {
      setApproving(false);
    }
  };

  const fetchTransactions = async () => {
    const custId = details?.cust_id || details?.user_id;
    if (!custId) return;
    setTxLoading(true);
    try {
      const response = await API.getCustomerDelipointTransaction({ cust_id: custId, order_id: orderId, page: 0, limit: 50 });
			const resp = await response.json();
      if (resp.success) {
        setTransactions(resp.data ?? []);
      }
    } catch (e) {
      console.error('Failed to fetch delipoint transactions', e);
    } finally {
      setTxLoading(false);
    }
  };

  const fetchCustomerDp = async () => {
    const custId = details?.cust_id || details?.user_id;
    if (!custId) return;
    try {
			const params = {
        cust_id: custId
			};
      const response = await API.getCustomerDelipointCurrent(params);
			const resp = await response.json();
      if (resp.success) {
        setCustomerDp(resp.data ?? null);
      }
    } catch (e) {
      console.error('Failed to fetch customer delipoints info', e);
    }
  };

  const handleItemEditChange = (key, field, value) => {
    setItemEdits(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  const getItemRedeemError = (edits) => {
    const hasPoints = edits.dp_points_redeemed !== '' && edits.dp_points_redeemed !== null && edits.dp_points_redeemed !== undefined;
    const hasValue = edits.dp_discount_payment !== '' && edits.dp_discount_payment !== null && edits.dp_discount_payment !== undefined;
    if (hasPoints && !hasValue) return 'value';
    if (!hasPoints && hasValue) return 'points';
    return null;
  };

  const hasRedeemErrors = (details?.order_item_details ?? []).some((item, i) => {
    const key = item.order_item_id ?? item.id ?? i;
    return getItemRedeemError(itemEdits[key] ?? {}) !== null;
  });

  const handleSavePromoPoints = async () => {
    if (hasRedeemErrors) return;
    setSavingPromo(true);
    try {
      const body = {
        order_id: orderId,
        promo_order_points: (parseInt(promoPoints) || 0) !== (parseInt(details?.dp_promo_points_earned) || 0)
          ? (parseInt(promoPoints) || 0)
          : null,
        order_item_details: (details?.order_item_details ?? []).map((item, i) => {
          const key = item.order_item_id ?? item.id ?? i;
          const edits = itemEdits[key] ?? {};
          return {
            order_item_id: item.order_item_id ?? item.id,
            product_multiplier: edits.dp_product_multiplier !== '' ? parseFloat(edits.dp_product_multiplier) : null,
            fixed_item_points: edits.dp_fix_points_promo_item !== '' ? parseInt(edits.dp_fix_points_promo_item) : null,
            redeemed_delipoints: edits.dp_points_redeemed !== '' ? parseInt(edits.dp_points_redeemed) : null,
            redeemed_value: edits.dp_discount_payment !== '' ? parseFloat(edits.dp_discount_payment) : null,
          };
        }),
      };
      const response = await API.updateOrderPromotionDetails(body);
      const resp = await response.json();
      if (resp.success) {
        setSaveAlert({ open: true, success: true, message: 'DeliPoints saved successfully.' });
        setTimeout(() => window.location.reload(), 1000);
      } else {
        setSaveAlert({ open: true, success: false, message: resp.message || 'Failed to save DeliPoints.' });
      }
    } catch (e) {
      console.error('Failed to save delipoints', e);
      setSaveAlert({ open: true, success: false, message: 'An error occurred while saving.' });
    } finally {
      setSavingPromo(false);
    }
  };

  // Derived summary values from details
  const itemPoints = details?.dp_item_points_earned ?? 0;

  const { sumItemRedeemedPoints, sumItemRedeemedValue } = (details?.order_item_details ?? []).reduce(
    (acc, item) => ({
      sumItemRedeemedPoints: acc.sumItemRedeemedPoints + (parseFloat(item.dp_points_redeemed) || 0),
      sumItemRedeemedValue:  acc.sumItemRedeemedValue  + (parseFloat(item.dp_discount_payment) || 0),
    }),
    { sumItemRedeemedPoints: 0, sumItemRedeemedValue: 0 }
  );

  const redeemedItemPromo      = sumItemRedeemedPoints;
  const redeemedItemPromoValue = sumItemRedeemedValue;
  const redeemedOrderPayment      = sumItemRedeemedPoints - (parseFloat(details?.dp_points_redeemed) || 0);
  const redeemedOrderPaymentValue = sumItemRedeemedValue - (parseFloat(details?.dp_discount_payment) || 0);
  const totalPoints = (details?.dp_points_earned ?? 0)
    + (parseInt(promoPoints) || 0)
    - (parseInt(details?.dp_promo_points_earned) || 0);

  const items = details?.order_item_details ?? [];

  const { totalValueForDp, totalFixedPoints, totalRedeemedPoints, ItemTotalDp } = items
    .filter(item => item.status !== 'cancelled')
    .reduce((acc, item, i) => {
      const key = item.order_item_id ?? item.id ?? i;
      const edits = itemEdits[key] ?? {};
      const itemPrice = parseFloat(item.item_price) || 0;
      const quantity = parseInt(item.quantity) || 0;
      const multiplier = parseFloat(edits.dp_product_multiplier) || 0;
      return {
        totalValueForDp: acc.totalValueForDp + itemPrice * quantity * multiplier,
        totalFixedPoints: acc.totalFixedPoints + (parseInt(edits.dp_fix_points_promo_item) || 0),
        totalRedeemedPoints: acc.totalRedeemedPoints + (parseInt(edits.dp_points_redeemed) || 0),
        ItemTotalDp: Math.floor((acc.totalValueForDp + itemPrice * quantity * multiplier) * (details?.dp_customer_multiplier ?? 1)),
      };
    }, { totalValueForDp: 0, totalFixedPoints: 0, totalRedeemedPoints: 0, ItemTotalDp: 0 });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gridGap={8}>
          <CardGiftcardIcon style={{ color: colors.primary }} />
          <Typography variant="h5" className="text-uppercase font-weight-bold mb-0">
            DeliPoints — Order #{orderId}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* ── Pending refund approvals ── */}
        {pendingTx.length > 0 && (
          <Box
            mb={3}
            p={2}
            style={{ backgroundColor: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6 }}
          >
            <Typography variant="h6" className="font-weight-bold mb-1" style={{ color: '#b26a00' }}>
              Pending refund approval
            </Typography>
            <Typography variant="body2" style={{ color: '#8d6e00', marginBottom: 8 }}>
              This order has {pendingTx.length} DeliPoints refund{pendingTx.length > 1 ? 's' : ''} awaiting your decision.
              Please approve or disapprove {pendingTx.length > 1 ? 'each one' : 'it'}.
            </Typography>
            <TableContainer component={Paper} elevation={0}>
              <Table size="small" className={classes.txTable}>
                <TableHead>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Points</TableCell>
                    <TableCell align="right">Value</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingTx.map((tx) => (
                    <TableRow key={tx.id}>
                      <TableCell>{tx.comment || '-'}</TableCell>
                      <TableCell align="right" style={{ fontWeight: 600 }}>{tx.points}</TableCell>
                      <TableCell align="right" style={{ color: '#e53935', fontWeight: 600 }}>
                        {tx.redeemed_value != null ? `$${parseFloat(tx.redeemed_value).toFixed(2)}` : '-'}
                      </TableCell>
                      <TableCell>{moment(tx.created_at).format('DD-MM-YYYY HH:mm')}</TableCell>
                      <TableCell align="center">
                        <Box display="flex" gridGap={8} justifyContent="center">
                          <Button
                            size="small"
                            variant="contained"
                            disableElevation
                            onClick={() => setConfirmDialog({ open: true, tx, status: 'approved' })}
                            style={{ backgroundColor: '#43a047', color: '#fff', textTransform: 'none' }}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setConfirmDialog({ open: true, tx, status: 'rejected' })}
                            style={{ color: '#e53935', borderColor: '#e53935', textTransform: 'none' }}
                          >
                            Disapprove
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* ── Row 1: Summary + Transactions ── */}
        <Row className="mb-3">

          {/* DeliPoints Summary */}
          <Col md={12} xl={5} className="pb-3">
            <Typography variant="h6" className="text-uppercase font-weight-bold mb-1">DeliPoints</Typography>
            <Row className="align-items-center">
              <Col xs={10}>
                <TableContainer component={Paper} elevation={0}>
                  <Table size="small" className={classes.summaryTable}>
                    <TableBody>
                      <TableRow>
                        <TableCell className={classes.summaryLabel}>Item points</TableCell>
                        <TableCell className={classes.summaryValue}>{itemPoints}</TableCell>
                      </TableRow>

                      {redeemedItemPromo !== 0 && (
                        <TableRow>
                          <TableCell className={classes.summaryLabel}>Redeemed points (item promo)</TableCell>
                          <TableCell className={`${classes.summaryValue} ${classes.redeem}`}>
                            -{redeemedItemPromo}
                            {redeemedItemPromoValue ? ` ($-${parseFloat(redeemedItemPromoValue).toFixed(2)})` : ''}
                          </TableCell>
                        </TableRow>
                      )}

                      {redeemedOrderPayment !== 0 && (
                        <TableRow>
                          <TableCell className={classes.summaryLabel}>Redeemed points (order payment)</TableCell>
                          <TableCell className={`${classes.summaryValue} ${classes.redeem}`}>
                            {redeemedOrderPayment}
                            {redeemedOrderPaymentValue ? ` ($${parseFloat(redeemedOrderPaymentValue).toFixed(2)})` : ''}
                          </TableCell>
                        </TableRow>
                      )}

                      <TableRow>
                        <TableCell className={classes.summaryLabel}>Order promo points</TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={promoPoints}
                            onChange={(e) => setPromoPoints(e.target.value)}
                            style={{ width: 70 }}
                            inputProps={{ min: 0 }}
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>

                      <TableRow className={classes.totalRow}>
                        <TableCell className={classes.summaryLabel}>Total</TableCell>
                        <TableCell className={classes.summaryValue}>{totalPoints}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Col>
              <Col xs={2} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Button
                  variant="contained"
                  onClick={handleSavePromoPoints}
                  disabled={savingPromo || hasRedeemErrors}
                  style={{
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    textTransform: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '8px 12px',
                  }}
                >
                  <div style={{display: "flex", flexDirection: "column", alignItems: "center", gap: 4}}>
                    {savingPromo
                      ? <CircularProgress size={40} style={{ color: '#9e9e9e' }} />
                      : <AutorenewIcon style={{ fontSize: 40, color: '#9e9e9e' }} />}

                    <Typography style={{ color: '#fff', lineHeight: 1, marginTop: 2, padding: '5px 15px', backgroundColor: colors.primary, borderRadius: 4 }}>
                      Save
                    </Typography>
                  </div>
                </Button>
              </Col>
            </Row>
            
            {customerDp && (
              <>
                <Typography variant="h6" className="text-uppercase font-weight-bold mt-3">
                  Customer
                  {customerDp.delipoints_tier ? ` — ${customerDp.delipoints_tier.name} tier (${customerDp.delipoints_tier.multiplier}x multiplier)` : ''}
                </Typography>
                <Box className={classes.tierBox}>
                  <Box display="flex" flexDirection="column" gridGap={6}>
                    <Typography variant="body2">
                      order value last {customerDp.delipoints_tier.period_months} months:&nbsp;
                      <strong>$&nbsp;{(customerDp.delipoints_tier.total_spend ?? 0).toLocaleString()}&nbsp;&nbsp;&nbsp;{customerDp.delipoints_tier.name}</strong>
                    </Typography>
                    <Typography variant="body2">
                      order value last {customerDp.delipoints_grace_tier.period_months} months:&nbsp;
                      <strong>$&nbsp;{(customerDp.delipoints_grace_tier.total_spend_excl_grace ?? 0).toLocaleString()}&nbsp;&nbsp;&nbsp;{customerDp.delipoints_grace_tier.name}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Current DeliPoints Balance:&nbsp;
                      <strong>{(customerDp.dp_balance ?? 0).toLocaleString()}</strong>
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
          </Col>

          {/* Transactions */}
          <Col md={12} xl={7} className="pb-3">
            <Typography variant="h6" className="text-uppercase font-weight-bold mb-1">Transactions</Typography>
            <TableContainer component={Paper} elevation={0} style={{ minHeight: 220, overflow: 'auto' }}>
              <Table size="small" stickyHeader className={classes.txTable}>
                <TableHead>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Points</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>User</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {txLoading ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : transactions.length > 0 ? (
                    transactions.map((tx, i) => {
                      const pointsDisplay = tx.redeemed_value
                        ? `${tx.points} ($${parseFloat(tx.redeemed_value).toFixed(2)})`
                        : tx.points;
                      const isNegative = tx.points < 0;
                      return (
                        <TableRow key={i}>
                          <TableCell>{tx.comment || tx.description || '-'}</TableCell>
                          <TableCell
                            align="right"
                            style={{ color: isNegative ? '#e53935' : '#333', fontWeight: 600 }}
                          >
                            {isNegative ? '' : '+'}{pointsDisplay}
                          </TableCell>
                          <TableCell>{tx.type || '-'}</TableCell>
                          <TableCell>{moment(tx.createdAt).format('DD-MM-YYYY HH:mm')}</TableCell>
                          <TableCell>
                            {tx.store_user
                              ? `${tx.store_user.first_name ?? ''} ${tx.store_user.last_name ?? ''}`.trim()
                              : 'System'}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">No transactions</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Col>
        </Row>

        <Divider style={{ marginBottom: 16 }} />

        {/* ── Row 3: Order Item Details ── */}
        <Typography variant="h6" className="text-uppercase font-weight-bold mb-1">Order Item Details</Typography>
        <TableContainer component={Paper} elevation={0}>
          <Table size="small" className={classes.itemTable}>
            <TableHead>
              <TableRow>
                <TableCell>SKU</TableCell>
                <TableCell>Item name</TableCell>
                <TableCell align="center">Quantity</TableCell>
                <TableCell align="right">Unit price</TableCell>
                <TableCell align="right">Coupon discount</TableCell>
                <TableCell align="center">Product multiplier</TableCell>
                <TableCell align="right">Value for DeliPoints</TableCell>
                <TableCell align="center">Fixed item points</TableCell>
                <TableCell align="center">Redeemed DeliPoints</TableCell>
                <TableCell align="right">Redeemed value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length > 0 ? (
                items.map((item, i) => {
                  const info = item.store_item_infos?.[0] ?? {};
                  const key = item.order_item_id ?? item.id ?? i;
                  const edits = itemEdits[key] ?? {};
                  const redeemError = getItemRedeemError(edits);
                  return (
                    <TableRow key={i} style={{ opacity: item.status === 'cancelled' ? 0.45 : 1 }}>
                      <TableCell>{info.product_code ?? '-'}</TableCell>
                      <TableCell>{item.item_name ?? '-'}</TableCell>
                      <TableCell align="center">{item.quantity ?? '-'}</TableCell>
                      <TableCell align="right">
                        {(() => {
                          const originalPrice = item.original_price != null ? parseFloat(item.original_price) : null;
                          const ItemPrice = parseFloat(item.item_price) || 0;
                          if (originalPrice == null) return '-';
                          if (ItemPrice > 0 && originalPrice > ItemPrice) {
                            return (
                              <Box display="flex" flexDirection="column" alignItems="flex-end">
                                <Typography style={{ textDecoration: 'line-through', color: '#999', lineHeight: 1.2 }}>
                                  ${originalPrice.toFixed(2)}
                                </Typography>
                                <Typography style={{ color: '#4caf50', fontWeight: 600, lineHeight: 1.2 }}>
                                  ${ItemPrice.toFixed(2)}
                                </Typography>
                              </Box>
                            );
                          }
                          return `$${originalPrice.toFixed(2)}`;
                        })()}
                      </TableCell>
                      <TableCell align="right">
                        {(() => {
                          const couponDiscount = parseFloat(item.coupon_discount_amount) || 0;
                          if (couponDiscount <= 0) return '-';
                          return (
                            <Typography style={{ color: '#e53935', fontWeight: 600, }}>
                              -${couponDiscount.toFixed(2)}
                            </Typography>
                          );
                        })()}
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          size="small"
                          type="number"
                          value={edits.dp_product_multiplier ?? ''}
                          onChange={(e) => handleItemEditChange(key, 'dp_product_multiplier', e.target.value)}
                          inputProps={{ min: 0, step: 0.1, style: { textAlign: 'center', width: 60 } }}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        {(() => {
                          const itemPrice = parseFloat(item.item_price) || 0;
                          const quantity = parseInt(item.quantity) || 0;
                          const multiplier = parseFloat(edits.dp_product_multiplier) || 0;
                          if (!multiplier) return '-';
                          return `$${(itemPrice * quantity * multiplier).toFixed(2)}`;
                        })()}
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          size="small"
                          type="number"
                          value={edits.dp_fix_points_promo_item ?? ''}
                          onChange={(e) => handleItemEditChange(key, 'dp_fix_points_promo_item', e.target.value)}
                          inputProps={{ min: 0, style: { textAlign: 'center', width: 60 } }}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          size="small"
                          type="number"
                          value={edits.dp_points_redeemed ?? ''}
                          onChange={(e) => handleItemEditChange(key, 'dp_points_redeemed', e.target.value)}
                          inputProps={{ min: 0, style: { textAlign: 'center', width: 60 } }}
                          variant="outlined"
                          error={redeemError === 'points'}
                          title={redeemError === 'points' ? 'Required when redeemed value is set' : ''}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          size="small"
                          type="number"
                          value={edits.dp_discount_payment ?? ''}
                          onChange={(e) => handleItemEditChange(key, 'dp_discount_payment', e.target.value)}
                          inputProps={{ min: 0, step: 0.01, style: { textAlign: 'center', width: 70 } }}
                          variant="outlined"
                          error={redeemError === 'value'}
                          title={redeemError === 'value' ? 'Required when redeemed points is set' : ''}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={11} align="center">No items</TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell colSpan={1} align="left">Total</TableCell>
                <TableCell colSpan={5} align="center"></TableCell>
                <TableCell colSpan={1} align="center">{totalValueForDp > 0 ? `$${totalValueForDp.toFixed(2)}` : '-'}</TableCell>
                <TableCell colSpan={3} align="center"></TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={1} align="left">Customer Multiplier</TableCell>
                <TableCell colSpan={5} align="center"></TableCell>
                <TableCell colSpan={1} align="center">{details.dp_customer_multiplier}</TableCell>
                <TableCell colSpan={3} align="center"></TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={1} align="left">Item Points</TableCell>
                <TableCell colSpan={5} align="center"></TableCell>
                <TableCell colSpan={1} align="center">{ItemTotalDp > 0 ? `${ItemTotalDp}` : '-'}</TableCell>
                <TableCell colSpan={1} align="center">{totalFixedPoints || '-'}</TableCell>
                <TableCell colSpan={1} align="center">{totalRedeemedPoints || '-'}</TableCell>
                <TableCell colSpan={1} align="center"></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined" style={{ color: '#666' }}>Close</Button>
      </DialogActions>

      {/* Approve / disapprove refund confirmation */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => !approving && setConfirmDialog({ open: false, tx: null, status: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {confirmDialog.status === 'approved' ? 'Approve refund?' : 'Disapprove refund?'}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" style={{ marginBottom: 8 }}>
            {confirmDialog.status === 'approved'
              ? 'This will approve the pending DeliPoints refund for this order.'
              : 'This will reject the pending DeliPoints refund for this order.'}
          </Typography>
          {confirmDialog.tx && (
            <Box p={1.5} style={{ backgroundColor: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: 6 }}>
              <Typography variant="body2"><strong>Points:</strong> {confirmDialog.tx.points}</Typography>
              <Typography variant="body2">
                <strong>Value:</strong>{' '}
                {confirmDialog.tx.redeemed_value != null ? `$${parseFloat(confirmDialog.tx.redeemed_value).toFixed(2)}` : '-'}
              </Typography>
              <Typography variant="body2"><strong>Note:</strong> {confirmDialog.tx.comment || '-'}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, tx: null, status: null })}
            variant="outlined"
            style={{ color: '#666' }}
            disabled={approving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmPending}
            variant="contained"
            disableElevation
            disabled={approving}
            style={{
              backgroundColor: confirmDialog.status === 'approved' ? '#43a047' : '#e53935',
              color: '#fff',
              textTransform: 'none',
            }}
          >
            {approving
              ? <CircularProgress size={20} style={{ color: '#fff' }} />
              : (confirmDialog.status === 'approved' ? 'Approve' : 'Disapprove')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={saveAlert.open}
        autoHideDuration={3000}
        onClose={() => setSaveAlert(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          onClose={() => setSaveAlert(prev => ({ ...prev, open: false }))}
          severity={saveAlert.success ? 'success' : 'error'}
          elevation={6}
          variant="filled"
        >
          {saveAlert.message}
        </MuiAlert>
      </Snackbar>
    </Dialog>
  );
};

export default DeliPointsDialog;
